import { useEffect, useState } from 'react';
import SideMenu from '../components/Menu';
import AddStudentModal from '../components/modals/AddStudentModal';
import { useNavigate } from 'react-router-dom';
import WarningModal from '../components/modals/WarningModal';

export interface Student {
  _id: string;
  studentId: string;
  userId?: string;
  fullName: string;
  universityEmail: string;
  institutionName: string;
  contactNumber: string | null;
  studentType: string | null;
  researchTitle: string | null;
  researchAbstract: string | null;
  researchStartDate: string;
  researchEndDate: string;
  progressLogs: object[];
}

const StudentManagement = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [warningModal, setWarningModal] = useState<boolean>(false);

  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://academichub-backend.onrender.com/students/get`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setLoading(false);

      if (!response.ok) {
        throw new Error('Failed to fetch students');
      }

      setStudents(await response.json());
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const deleteStudent = (student: Student) => async () => {
    console.log(student);
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/students/remove/${student.studentId}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete student');
      }

      fetchData();
    } catch (error) {
      console.error('Error deleting student:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const visibleFields = [
    'fullName',
    'universityEmail',
    'studentType',
    'researchTitle',
    'researchStartDate',
    'researchEndDate',
  ];

  const activateEditModal = (student: Student) => {
    if (student) {
      setSelectedStudent(student);
      console.log(student);
      setModalVisible(true);
    }
  };

  const activateAddModal = () => {
    setSelectedStudent(null);
    setModalVisible(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen w-screen">
        <i className="fa-solid fa-spinner animate-spin text-blue-500 text-3xl"></i>
      </div>
    );
  }

  return (
    <>
      <SideMenu />
      <div id="student-management" className="w-full h-screen bg-accent p-3 overflow-hidden">
        <div className="bg-white rounded-lg h-full flex flex-col p-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-center">Student Management</h1>
            <button
              onClick={() => activateAddModal()}
              className="text-xs bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-gray-800"
            >
              Add Student
            </button>
          </div>
          <div className="overflow-auto mt-4">
            <table className="min-w-full bg-white">
              <thead className="sticky top-0 bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-xs border-b">Name</th>
                  <th className="px-4 py-2 text-xs border-b">Email</th>
                  <th className="px-4 py-2 text-xs border-b">Type</th>
                  <th className="px-4 py-2 text-xs border-b">Research Title</th>
                  <th className="px-4 py-2 text-xs border-b">Start Date</th>
                  <th className="px-4 py-2 text-xs border-b">End Date</th>
                  <th className="px-4 py-2 text-xs border-b">Linked</th>
                  <th className="px-4 py-2 text-xs border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => {
                  const isLinked = !!student.userId;
                  return (
                    <tr
                      key={student._id}
                      onClick={() => {
                        if (isLinked) {
                          navigate(`/papers/unpublished/${student.userId}`);
                        }
                      }}
                      className={`${
                        isLinked ? 'cursor-pointer hover:bg-gray-100' : ''
                      } transition-colors`}
                    >
                      {visibleFields.map(field => (
                        <td key={field} className="px-4 py-2 text-xs border-b text-center">
                          {field.toLowerCase().includes('date')
                            ? new Date(
                                student[field as keyof Student] as string
                              ).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: '2-digit',
                                year: '2-digit',
                              })
                            : (student[field as keyof Student] as string) || 'N/A'}
                        </td>
                      ))}
                      <td className="px-4 py-2 text-xs border-b text-center">
                        {isLinked ? (
                          <i className="fa-solid fa-check text-green-500"></i>
                        ) : (
                          <i className="fa-solid fa-xmark text-red-500"></i>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs border-b text-center">
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            activateEditModal(student);
                          }}
                          className="text-blue-500 hover:underline"
                        >
                          Edit
                        </button>
                        <br />
                        <button
                          onClick={() => {
                            setWarningModal(true);
                            setSelectedStudent(student);
                          }}
                          className="text-red-500 hover:underline"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <AddStudentModal
        onClose={() => setModalVisible(false)}
        visible={modalVisible}
        initialValues={selectedStudent || undefined}
        onStudentAdded={() => {
          fetchData();
        }}
      />{' '}
      <WarningModal
        visible={warningModal}
        description="Are you sure you want to delete this student?"
        onCancel={() => setWarningModal(false)}
        onConfirm={() => {
          if (selectedStudent) {
            deleteStudent(selectedStudent)();
          }
          setWarningModal(false);
          setSelectedStudent(null);
        }}
      />
    </>
  );
};
export default StudentManagement;
